import { h, JSX } from 'preact';
import { AllMeetingsTableItem, DateFormats, EventStatus, MeetingStatuses } from 'core.frontend';
import { AllMeetingsTable as FrontEndAllMeetingsTable } from 'core.frontend';
import ObservingComponent from '../componentBases/observingComponent';
import Card from '../containers/card';
import SmartTable from '../smartTable/smartTable';
import SmartTableColumn from '../smartTable/smartTableColumn';
import Button from '../buttons/button';
import FormattedDate from '../text/formattedDate';
import MeetingDetailForm from './meetingDetailForm'
import MeetingCreatedByLabel from './meetingCreatedByLabel';
import MeetingAttendeesLabel from './meetingAttendeesLabel';
import ToggleSwitchField from '../fields/toggleSwitchField';
import Field from '../fields/field';

export interface AllMeetingsTableProps {
    table: FrontEndAllMeetingsTable;
}

class AllMeetingsTable extends ObservingComponent<AllMeetingsTableProps> {
    public componentWillMount() {
        this.registerUpdateObserver(this.props.table.observationProvider);
    }

    private meetingDetailsTemplate = (item: AllMeetingsTableItem) => {
        if (item.expandedContent) {
            return (
                <div class="d-block text-sm">
                    <div class="d-block">
                        <b>Name: </b>{item.name}
                    </div>

                    <div class="d-block">
                        <b>Venue: </b>{item.expandedContent.assignedVenue}
                    </div>

                    <div class="flex">
                        <MeetingCreatedByLabel meeting={item.expandedContent} />
                    </div>

                    {item.expandedContent.subject &&
                        <div class="d-block">
                            <b>Subject: </b>{item.expandedContent.subject}
                        </div>
                    }

                    <div class="d-block">
                        <b>Meeting Category: </b>{item.expandedContent.defaultMeetingCategory}
                        {item.expandedContent.scheduledBy &&
                            <span> / {item.expandedContent.scheduledBy}</span>
                        }
                    </div>

                    <div class="d-block">
                        <b>Food Needed: </b>{item.expandedContent.foodNeeded ? 'Yes' : 'No' }
                    </div>

                    <div class="d-block">
                        <b>Table(s): </b> {item.expandedContent.tableAssignments.map((table, index) => <span>{table.name + (index + 1 < item.expandedContent.tableAssignments.length ? ', ' : '')}</span>)}
                    </div>

                    <div class="d-block">
                        <b>Attendees: </b><MeetingAttendeesLabel meeting={item.expandedContent} />
                    </div>

                    <div class="d-block">
                        <b>Customers: </b>

                        <div>
                            {(!item.expandedContent.guests || !item.expandedContent.guests.length) &&
                                <span>None</span>
                            }

                            {!!item.expandedContent.guests && !!item.expandedContent.guests.length &&
                                item.expandedContent.guests.map((guest, index) => (
                                    <div>

                                        {!!guest.rank &&
                                            <span>{guest.rank} </span>
                                        }

                                        {!!guest.title &&
                                            <span> ({guest.title}) </span>
                                        }

                                        {guest.fullName}

                                        <span> - </span>
                                        {guest.companyName}

                                        {!!guest.isLead &&
                                            <span> (L)</span>
                                        }

                                        {index + 1 < item.expandedContent.guests.length &&
                                            <span>&nbsp; | &nbsp;</span>
                                        }
                                    </div>
                                ))
                            }
                        </div>
                    </div>

                    <div class="d-block">
                        <b>Comments: </b>{item.expandedContent.printedComments}
                    </div>
                </div>
            );
        } else {
            return (
                <div class="d-block">
                    <div className="loading-placeholder shimmer d-block"></div>
                    <div className="loading-placeholder shimmer d-block"></div>
                    <div className="loading-placeholder shimmer d-block"></div>
                    <div className="loading-placeholder shimmer d-block"></div>
                    <div className="loading-placeholder shimmer d-block"></div>
                    <div className="loading-placeholder shimmer d-block"></div>
                </div>
            );
        }
    }

    private getCustomerCompanyNames = (item: AllMeetingsTableItem): JSX.Element => {
        if (!item.customerCompanyNames) {
            return <i>N/A</i>;
        }

        const customerCompanies = item.customerCompanyNames.split('|');

        return (
            <span>
                {customerCompanies.map((company, index) => (
                    <span>
                        <span className="no-wrap">{company.trim()}</span>
                        {(index + 1) < customerCompanies.length ? <br/> : ''}
                    </span>
                ))}
            </span>);
    }

    private getCustomerNames = (item: AllMeetingsTableItem): JSX.Element => {
        if (!item.customerNames) {
            return <span></span>;
        }

        const customers = item.customerNames.split('|');

        return (
            <span>
                {customers.map((customer, index) => (
                    <span>
                        <span className="no-wrap">{customer.trim()}</span>
                        {(index + 1) < customers.length ? <br/> : ''}
                    </span>
                ))}
            </span>);
    }

    private buildColumns = (): SmartTableColumn<AllMeetingsTableItem>[] => {
        const { table: page } = this.props;

        const customerColumns = [];

        if (!page.isCustomerView) {
            customerColumns.push({
                className: "d-none text-center d-md-table-cell text-sm fit-content pt-0 pb-0 ",
                headingClassName: "fit-content",
                heading: 'Company',
                cellTemplate: (item, index) => this.getCustomerCompanyNames(item)
            },
            {
                className: "d-none text-center d-md-table-cell text-sm fit-content pt-0 pb-0 ",
                headingClassName: "fit-content",
                heading: 'Customers',
                cellTemplate: (item, index) => this.getCustomerNames(item)
            });
        }

        return [
            {
                heading: 'Event',
                sortExpression: 'Events.name',
                className: 'd-none d-md-table-cell text-sm fit-content pt-0 pb-0',
                cellTemplate: (item, index) =>
                    (<span>
                        <span className="text-sm">{item.eventName}</span>
                        
                        <br/>
                        
                        <span class="text-sm">{item.eventCity}, {item.eventCountry}</span>

                        {item.eventLeadCompany &&
                            <span class="d-md-none sm">
                                <br/>
                                {item.eventLeadCompany}
                            </span>
                        }

                        {item.eventStatus === EventStatus.Archived &&
                            <span class="text-red text-sm"><br/> Archived</span>
                        }

                        {item.eventStatus === EventStatus.Deleted &&
                            <span class="text-red text-sm"><br/> Deleted</span>
                        }
                    </span>),
                appendSavingIndicator: true
            },
            {
                heading: 'Meeting Name',
                sortExpression: 'name',
                cellTemplate: (item, index) => (
                    <span className="text-increase-phone">
                        <span>
                            <span className="d-md-none">
                                <Button
                                    className="btn-link"
                                    button={page.buildMeetingDetailButton(item, true)} />
                            </span>

                            <Button
                                className="btn-link d-none d-md-inline"
                                button={page.buildMeetingDetailButton(item, false)} />
                            
                            {!item.isActive &&
                                <span className="text-sm text-red">
                                   <span> - </span>
                                    Inactive
                                </span>
                            }
                        </span>

                        <span className="d-md-none">
                            <br/>

                            <FormattedDate
                                date={item.startDateTime}
                                format={DateFormats.MMDDYYYY} />

                            <br />
                            
                            Guests Arrived: {item.guestArrived ? 
                                <span class="text-green">Yes</span> :
                                <span class="text-red">No</span>
                            }
                        </span>

                        <span className="d-block">
                            <span className="d-md-none fill text-right">
                                Customer Companies: {this.getCustomerCompanyNames(item)}
                            </span>
                        </span>

                        <div className="d-block d-md-none">
                            <div className="mt-1">{item.eventName}</div>
                            <br/>
                            <span class="text-sm">{item.eventCity}, {item.eventCountry}</span>

                            {item.eventLeadCompany &&
                                <span class="d-md-none sm">
                                    <br/>
                                    {item.eventLeadCompany}
                                </span>
                            }

                            {item.eventStatus === EventStatus.Archived &&
                                <span class="text-red text-sm"><br/> Archived</span>
                            }

                            {item.eventStatus === EventStatus.Deleted &&
                                <span class="text-red text-sm"><br/> Deleted</span>
                            }
                        </div>

                        <div className="d-block d-md-none">
                            <div className="mt-1">My Notes Count: {item.myNotesCount}</div>
                            <br/>
                            <span>Other Notes: {item.othersNotesCount}</span>
                        </div>
                    </span>
                )
            },
            ...customerColumns,
            {
                className: "d-none d-md-table-cell fit-content text-center text-sm",
                heading: 'Date',
                sortExpression: 'StartDateTime',
                cellTemplate: (item) =>
                    <FormattedDate
                        date={item.startDateTime}
                        format={DateFormats.MMDDYYYY} />
            },
            {
                className: "d-none d-md-table-cell fit-content text-center text-sm",
                heading: 'Arrived',
                sortExpression: 'Meetings.GuestArrived',
                cellTemplate: (item) =>
                    <span>
                        {item.guestArrived ? 
                            <span class="text-green">Yes</span> :
                            <span class="text-red">No</span>
                        }
                    </span>
            },
            {
                className: "d-none d-md-table-cell text-sm fit-content pt-0 pb-0",
                heading: 'Notes',
                cellTemplate: (item) => 
                    <span class="flex">
                        <span class="fill">
                            <span>My Notes Count: {item.myNotesCount}</span>
                            <br/>
                            <span>Other Notes: {item.othersNotesCount}</span>
                        </span>

                        {(item.myNotesCount > 0 || item.othersNotesCount > 0) && 
                         (item.currentUserInMeeting || item.currentUserCanViewAllEventNotes || item.currentUserCanViewAllNotes) &&
                            <a 
                                className="v-center ml-1"
                                target="_blank" 
                                href={page.buildMeetingNotesReportUrl(item)}>
                                    
                                View Notes
                            </a>
                        }
                    </span>
            }
        ];
    }

    public render({ table }: AllMeetingsTableProps): JSX.Element {
        return (
            <div class="w-100">
                <Card
                    headingLeftContent={
                        <div className="button-container fill text-sm">
                            {!table.isCustomerView &&
                                <i>You are currently filtering this data. Click the red X next to the filter above to remove the filter.</i>
                            }
                        </div>
                    }
                    headingRightContent={
                        <div className="button-container flex">
                            <ToggleSwitchField
                                field={table.myNotesSwitchField}
                                className="v-center mr-1" />

                            <Field
                                field={table.eventSearchField}
                                className="v-center inline-label text-sm" />
                        </div>
                    }>

                    <SmartTable
                        table={table.table}
                        columns={this.buildColumns()}
                        listOptionSlideContainer={table.listOptionSlideContainer} 
                        expandedContentTemplate={this.meetingDetailsTemplate}/>
                </Card>

                {table.meetingDetailForm && 
                    <MeetingDetailForm meetingDetailForm={table.meetingDetailForm} />
                }
            </div>
        );
    
    }
}

export default AllMeetingsTable;
